import { type FormEvent, useState } from 'react';
import { useApi, useConsulta } from '@plataforma/web/api';
import { fechaCorta, fechaHoraLocal, mxn } from '../formato';
import type { Vehiculo } from '../paginas/tipos';
import { nombreVehiculo } from '../paginas/Tablero';

interface Valuacion {
  id: string;
  fuente: string;
  versionAuto: string;
  monto: { cantidad: string; moneda: string; equivalenteMxn: string };
  ajustes: { concepto: string; cantidad: string }[];
  valorAjustadoMxn: string;
  fecha: string;
}

/** Valuación «sin siniestro» del vehículo (manual por ahora; Libro Azul cuando haya licencia). */
export function ValuacionVehiculo({ v, alGuardar }: { v: Vehiculo; alGuardar: () => void }) {
  const api = useApi();
  const { datos, recargar } = useConsulta<Valuacion[]>(`/finance/vehiculos/${v.id}/valuaciones`);
  const [error, setError] = useState<string | null>(null);
  const [abierto, setAbierto] = useState(false);
  const ultima = datos?.[0];
  const enviar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const ajustes = String(f.get('ajustes') ?? '')
      .split('\n')
      .map((l) => l.split(':').map((x) => x.trim()))
      .filter(([c, m]) => c && m)
      .map(([concepto, cantidad]) => ({
        concepto: concepto as string,
        cantidad: String(cantidad).replace(/[$,\s]/g, ''),
      }));
    setError(null);
    try {
      await api('POST', `/finance/vehiculos/${v.id}/valuaciones`, {
        versionAuto: f.get('versionAuto'),
        cantidad: String(f.get('cantidad')),
        moneda: f.get('moneda'),
        fecha: f.get('fecha'),
        ajustes,
      });
      setAbierto(false);
      recargar();
      alGuardar();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  return (
    <section className="seccion">
      <header>
        <h2>Valuación sin siniestro</h2>
        <div style={{ flex: 1 }} />
        {!abierto && (
          <button className="boton" onClick={() => setAbierto(true)}>
            {ultima ? 'Nueva valuación' : 'Valuar'}
          </button>
        )}
      </header>
      {ultima && !abierto && (
        <dl className="cuerpo resumen">
          <div>
            <dt>Valor de referencia</dt>
            <dd>{mxn(ultima.monto.equivalenteMxn)}</dd>
          </div>
          {ultima.ajustes.map((a) => (
            <div key={a.concepto}>
              <dt>{a.concepto}</dt>
              <dd>{mxn(a.cantidad)}</dd>
            </div>
          ))}
          <div>
            <dt>Valor esperado de venta</dt>
            <dd>{mxn(ultima.valorAjustadoMxn)}</dd>
          </div>
          <div>
            <dt>Fuente y fecha</dt>
            <dd>
              {ultima.fuente} · {fechaCorta(ultima.fecha)}
            </dd>
          </div>
        </dl>
      )}
      {!ultima && !abierto && (
        <p className="vacio">Sin valuación: sin ella no hay margen esperado.</p>
      )}
      {abierto && (
        <form className="cuerpo formulario" onSubmit={(e) => void enviar(e)}>
          <label className="campo">
            <span>Versión del auto</span>
            <input
              name="versionAuto"
              required
              minLength={2}
              maxLength={200}
              defaultValue={nombreVehiculo(v)}
            />
          </label>
          <label className="campo">
            <span>Valor sin siniestro</span>
            <input name="cantidad" required inputMode="decimal" pattern="\d{1,12}(\.\d{1,2})?" />
          </label>
          <label className="campo">
            <span>Moneda</span>
            <select name="moneda" defaultValue="MXN">
              <option value="MXN">MXN</option>
              <option value="USD">USD (FIX de la fecha)</option>
            </select>
          </label>
          <label className="campo">
            <span>Fecha</span>
            <input
              name="fecha"
              type="date"
              required
              defaultValue={fechaHoraLocal(new Date()).slice(0, 10)}
            />
          </label>
          <label className="campo" style={{ gridColumn: '1 / -1' }}>
            <span>Ajustes, uno por renglón «Concepto: cantidad» (negativo resta)</span>
            <textarea name="ajustes" rows={2} placeholder="Kilometraje alto: -10000" />
          </label>
          {error && (
            <p className="error" style={{ gridColumn: '1 / -1' }}>
              {error}
            </p>
          )}
          <div className="acciones">
            <button type="button" className="boton" onClick={() => setAbierto(false)}>
              Cancelar
            </button>
            <button className="boton primario">Guardar valuación</button>
          </div>
        </form>
      )}
    </section>
  );
}
